import React from 'react';
import { Card, CardContent } from '../ui/Card';
import { formatCurrency } from '../../lib/utils';
import { FlameIcon, ClockIcon, CalendarIcon, TrendingUpIcon, ZapIcon, UsersIcon } from 'lucide-react';
import type { PeakDay, PeakHour, ChannelBreakdown } from '../../lib/metrics-hooks';
interface MetricsPeakInsightsProps {
  peakDays: PeakDay[];
  peakHours: PeakHour[];
  channelBreakdown: ChannelBreakdown;
  totalOrders: number;
  totalRevenue: number;
  averageTicket: number;
}
export function MetricsPeakInsights({
  peakDays,
  peakHours,
  channelBreakdown,
  totalOrders,
  totalRevenue,
  averageTicket
}: MetricsPeakInsightsProps) {
  const totalChannel = channelBreakdown.qr + channelBreakdown.waiter + channelBreakdown.other;
  const qrPct = totalChannel > 0 ? Math.round(channelBreakdown.qr / totalChannel * 100) : 0;
  const waiterPct = totalChannel > 0 ? Math.round(channelBreakdown.waiter / totalChannel * 100) : 0;
  return <div className="space-y-4" data-id="element-618">
      {/* Summary Row */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4" data-id="element-619">
        <Card data-id="element-620">
          <CardContent className="p-5" data-id="element-621">
            <div className="flex items-start gap-3" data-id="element-622">
              <div className="w-10 h-10 bg-emerald-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-623">
                <TrendingUpIcon className="w-5 h-5 text-emerald-600" data-id="element-624" />
              </div>
              <div className="min-w-0" data-id="element-625">
                <p className="text-sm text-gray-500 font-medium" data-id="element-626">
                  Ingresos Totales
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-627">
                  {formatCurrency(totalRevenue)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-628">
          <CardContent className="p-5" data-id="element-629">
            <div className="flex items-start gap-3" data-id="element-630">
              <div className="w-10 h-10 bg-blue-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-631">
                <UsersIcon className="w-5 h-5 text-blue-600" data-id="element-632" />
              </div>
              <div className="min-w-0" data-id="element-633">
                <p className="text-sm text-gray-500 font-medium" data-id="element-634">
                  Total Órdenes
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5" data-id="element-635">
                  {totalOrders}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card data-id="element-636">
          <CardContent className="p-5" data-id="element-637">
            <div className="flex items-start gap-3" data-id="element-638">
              <div className="w-10 h-10 bg-purple-50 rounded-lg flex items-center justify-center flex-shrink-0" data-id="element-639">
                <ZapIcon className="w-5 h-5 text-purple-600" data-id="element-640" />
              </div>
              <div className="min-w-0" data-id="element-641">
                <p className="text-sm text-gray-500 font-medium" data-id="element-642">
                  Ticket Promedio
                </p>
                <p className="text-2xl font-bold text-gray-900 mt-0.5 truncate" data-id="element-643">
                  {formatCurrency(averageTicket)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Peak Insights Row */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4" data-id="element-644">
        {/* Peak Day */}
        <Card data-id="element-645">
          <CardContent className="p-5" data-id="element-646">
            <div className="flex items-center gap-2 mb-3" data-id="element-647">
              <div className="w-8 h-8 bg-red-50 rounded-lg flex items-center justify-center" data-id="element-648">
                <FlameIcon className="w-4 h-4 text-red-500" data-id="element-649" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-650">
                Día Pico
              </span>
            </div>
            {peakDays.length > 0 ? <div className="space-y-2" data-id="element-651">
                {peakDays.map((day, i) => <div key={day.date} className="flex items-center justify-between" data-id="element-652">
                    <div className="flex items-center gap-2" data-id="element-653">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-red-100 text-red-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-654">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-655">{day.label}</span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-656">
                      {day.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-657">Sin datos</p>}
          </CardContent>
        </Card>

        {/* Peak Hour */}
        <Card data-id="element-658">
          <CardContent className="p-5" data-id="element-659">
            <div className="flex items-center gap-2 mb-3" data-id="element-660">
              <div className="w-8 h-8 bg-amber-50 rounded-lg flex items-center justify-center" data-id="element-661">
                <ClockIcon className="w-4 h-4 text-amber-600" data-id="element-662" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-663">
                Hora Pico
              </span>
            </div>
            {peakHours.length > 0 ? <div className="space-y-2" data-id="element-664">
                {peakHours.map((hour, i) => <div key={hour.hour} className="flex items-center justify-between" data-id="element-665">
                    <div className="flex items-center gap-2" data-id="element-666">
                      <span className={`w-5 h-5 rounded-full text-xs font-bold flex items-center justify-center ${i === 0 ? 'bg-amber-100 text-amber-700' : 'bg-gray-100 text-gray-500'}`} data-id="element-667">
                        {i + 1}
                      </span>
                      <span className="text-sm text-gray-700" data-id="element-668">
                        {hour.label}
                      </span>
                    </div>
                    <span className="text-sm font-semibold text-gray-900" data-id="element-669">
                      {hour.orders}
                    </span>
                  </div>)}
              </div> : <p className="text-sm text-gray-400" data-id="element-670">Sin datos</p>}
          </CardContent>
        </Card>

        {/* QR Self-service */}
        <Card data-id="element-671">
          <CardContent className="p-5" data-id="element-672">
            <div className="flex items-center gap-2 mb-3" data-id="element-673">
              <div className="w-8 h-8 bg-emerald-50 rounded-lg flex items-center justify-center" data-id="element-674">
                <CalendarIcon className="w-4 h-4 text-emerald-600" data-id="element-675" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-676">
                Autoservicio QR
              </span>
            </div>
            <div className="space-y-1" data-id="element-677">
              <p className="text-2xl font-bold text-emerald-600" data-id="element-678">
                {channelBreakdown.qr}
              </p>
              <p className="text-sm text-gray-500" data-id="element-679">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-680">{qrPct}%</span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-681">
                {formatCurrency(channelBreakdown.qrRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Waiter */}
        <Card data-id="element-682">
          <CardContent className="p-5" data-id="element-683">
            <div className="flex items-center gap-2 mb-3" data-id="element-684">
              <div className="w-8 h-8 bg-blue-50 rounded-lg flex items-center justify-center" data-id="element-685">
                <UsersIcon className="w-4 h-4 text-blue-600" data-id="element-686" />
              </div>
              <span className="text-sm font-semibold text-gray-900" data-id="element-687">
                Mesero
              </span>
            </div>
            <div className="space-y-1" data-id="element-688">
              <p className="text-2xl font-bold text-blue-600" data-id="element-689">
                {channelBreakdown.waiter}
              </p>
              <p className="text-sm text-gray-500" data-id="element-690">
                pedidos ·{' '}
                <span className="font-semibold text-gray-700" data-id="element-691">
                  {waiterPct}%
                </span>{' '}
                del total
              </p>
              <p className="text-xs text-gray-400" data-id="element-692">
                {formatCurrency(channelBreakdown.waiterRevenue)}
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>;
}